import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Save, Loader2 } from "lucide-react";
import PricingEditor from "./PricingEditor";
import { getIcon } from "./ServiceCard";

export default function ServiceConfigDrawer({
  open,
  service,
  hostService,
  properties,
  onClose,
  onToggle,
  onPricingChange,
  onPropertyScopeChange,
  onSave,
  saving,
}) {
  const [localEnabled, setLocalEnabled] = useState(false);
  const [localPricing, setLocalPricing] = useState({});
  const [localPropertyIds, setLocalPropertyIds] = useState([]);
  const [applyToAll, setApplyToAll] = useState(true);

  useEffect(() => {
    if (hostService) {
      setLocalEnabled(!!hostService.enabled);
      setLocalPricing(hostService.pricing || {});
      const ids = hostService.propertyIds || [];
      setLocalPropertyIds(ids);
      setApplyToAll(ids.length === 0);
    } else {
      setLocalEnabled(false);
      setLocalPricing({});
      setLocalPropertyIds([]);
      setApplyToAll(true);
    }
  }, [hostService, open]);

  if (!service) return null;

  const Icon = getIcon(service.key);
  const isProvider =
    `${service.key || ""} ${service.name || ""}`.toLowerCase().match(/uber|ride|transport|chef/);

  const handleToggle = () => {
    const next = !localEnabled;
    setLocalEnabled(next);
    onToggle(service.key, next);
  };

  const handlePricingUpdate = (nextPricing) => {
    setLocalPricing(nextPricing);
    onPricingChange(service.key, nextPricing);
  };

  const handleScopeAll = (checked) => {
    setApplyToAll(checked);
    if (checked) {
      setLocalPropertyIds([]);
      onPropertyScopeChange(service.key, [], true);
    }
  };

  const handlePropertyToggle = (propertyId, checked) => {
    const next = new Set(localPropertyIds);
    if (checked) next.add(propertyId);
    else next.delete(propertyId);
    const arr = Array.from(next);
    setLocalPropertyIds(arr);
    onPropertyScopeChange(service.key, arr, false);
  };

  const startSelectingProperties = () => {
    const allIds = (properties || []).map((p) => p._id);
    setApplyToAll(false);
    setLocalPropertyIds(allIds);
    onPropertyScopeChange(service.key, allIds, false);
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-40 flex">
          <motion.div
            className="flex-1 bg-black/40"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          />
          <motion.div
            initial={{ x: 540, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 560, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 34, opacity: { duration: 0.22 } }}
            className="w-full max-w-[480px] h-full overflow-hidden rounded-l-2xl flex flex-col bg-white shadow-xl"
          >
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600">
                  <Icon size={18} strokeWidth={1.8} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate text-sm">
                    {service.name || service.key}
                  </h3>
                  <p className="text-xs text-gray-400 truncate">
                    {service.category || "Service"}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-center transition-colors"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Enable/disable */}
              <div className="flex items-center justify-between rounded-xl bg-gray-50 border border-gray-200 p-3.5">
                <div>
                  <p className="text-sm font-medium text-gray-900">Enable this service</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {localEnabled
                      ? "Guests can discover and book this service"
                      : "Not visible to guests"}
                  </p>
                </div>
                <button
                  onClick={handleToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    localEnabled ? "bg-primary-500" : "bg-gray-300"
                  }`}
                >
                  <motion.span
                    className="inline-block h-5 w-5 rounded-full bg-white shadow"
                    animate={{ x: localEnabled ? 21 : 2 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                </button>
              </div>

              {/* Pricing */}
              {isProvider ? (
                <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
                  <p className="text-sm text-gray-600">
                    Pricing is managed by the service provider. Guests see real-time quotes.
                    You control availability only.
                  </p>
                </div>
              ) : (
                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Guest pricing</h4>
                  <PricingEditor value={localPricing} onChange={handlePricingUpdate} />
                </div>
              )}

              {/* Property scope */}
              <div className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-900">Property scope</h4>
                  <label className="inline-flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={applyToAll}
                      onChange={(e) => handleScopeAll(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    All properties
                  </label>
                </div>

                {!applyToAll && localPropertyIds.length > 0 && (
                  <div className="space-y-1.5 max-h-40 overflow-auto">
                    {(properties || []).map((p) => {
                      const selected = localPropertyIds.includes(p._id);
                      return (
                        <label
                          key={p._id}
                          className="flex items-center justify-between rounded-lg px-3 py-2 bg-gray-50 border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <span className="text-xs text-gray-700 truncate">
                            {p.listingTitle || p.address?.line1 || p._id}
                          </span>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => handlePropertyToggle(p._id, e.target.checked)}
                            className="rounded border-gray-300"
                          />
                        </label>
                      );
                    })}
                  </div>
                )}

                {applyToAll && (
                  <button
                    onClick={startSelectingProperties}
                    className="mt-2 text-xs font-medium text-gray-500 hover:text-gray-700 underline underline-offset-2 transition-colors"
                  >
                    Choose specific properties instead
                  </button>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Done
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:bg-primary-600 transition-colors shadow-bellhopt disabled:opacity-60"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save size={14} />
                )}
                Save Changes
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
